import React, { useState, useEffect } from 'react';
import { api, AdminUserPublic, AdminInvitationPublic } from '../../utils/api';
import { Organisation } from '../../types';
import { useAuth } from '../../contexts/AuthProvider';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon,
  XMarkIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

export const AdminUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUserPublic[]>([]);
  const [invitations, setInvitations] = useState<AdminInvitationPublic[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const inviteRole = 'site_admin' as const;
  const [inviting, setInviting] = useState(false);

  // Edit modal
  const [editUser, setEditUser] = useState<AdminUserPublic | null>(null);
  const [editName, setEditName] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editOrgIds, setEditOrgIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Invitations section
  const [showInvitations, setShowInvitations] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersData, invData, orgsData] = await Promise.all([
        api.getAdminUsers(),
        api.getAdminInvitations(),
        api.getOrganisations(),
      ]);
      setUsers(usersData);
      setInvitations(invData);
      setOrganisations(orgsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    setInviting(true);
    try {
      await api.inviteAdminUser({
        email: inviteEmail.trim(),
        name: inviteName.trim(),
        role: inviteRole,
      });
      setInviteEmail('');
      setInviteName('');
      setShowInviteForm(false);
      showSuccess('Invitation sent successfully');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      setError(null);
      await api.resendAdminInvitation(invitationId);
      showSuccess('Invitation resent successfully');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!window.confirm('Cancel this invitation?')) return;
    try {
      setError(null);
      await api.cancelAdminInvitation(invitationId);
      showSuccess('Invitation cancelled');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  const openEditModal = (u: AdminUserPublic) => {
    setEditUser(u);
    setEditName(u.name);
    setEditIsActive(u.isActive);
    setEditOrgIds(u.orgAccess.map((o) => o.orgId));
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateAdminUser(editUser.id, {
        name: editName.trim(),
        isActive: editIsActive,
        orgAssignments: editOrgIds.map((orgId) => ({
          orgId,
          orgRole: 'org_full_admin' as const,
        })),
      });
      setEditUser(null);
      showSuccess('User updated successfully');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (currentUser?.id === id) {
      setError('Cannot delete your own account');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      setError(null);
      await api.deleteAdminUser(id);
      showSuccess('User deleted');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleToggleActive = async (u: AdminUserPublic) => {
    if (currentUser?.id === u.id) {
      setError('Cannot deactivate your own account');
      return;
    }
    try {
      setError(null);
      await api.updateAdminUser(u.id, { isActive: !u.isActive });
      showSuccess(u.isActive ? 'User deactivated' : 'User reactivated');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    }
  };

  const getOrgName = (orgId: string) => organisations.find((o) => o.id === orgId)?.name || orgId;

  const roleLabel = (role: string) => {
    switch (role) {
      case 'site_admin':
        return 'Site Admin';
      case 'org_admin':
        return 'Org Admin';
      case 'partner':
        return 'Partner';
      default:
        return role;
    }
  };

  const roleBadgeClass = (role: string) => {
    switch (role) {
      case 'site_admin':
        return 'badge-primary';
      case 'org_admin':
        return 'badge-secondary';
      case 'partner':
        return 'badge-accent';
      default:
        return 'badge-ghost';
    }
  };

  const pendingInvitations = invitations.filter((inv) => inv.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-base-content">Admin</h1>
          <p className="text-sm text-base-content/60 mt-1">Manage admin users and invitations</p>
        </div>
        <button className="btn btn-primary btn-sm gap-2" onClick={() => setShowInviteForm(true)}>
          <EnvelopeIcon className="h-4 w-4" />
          Invite User
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error text-sm">
          <span>{error}</span>
          <button className="btn btn-ghost btn-xs" onClick={() => setError(null)}>
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success text-sm">
          <span>{successMessage}</span>
        </div>
      )}

      {/* Invite Form */}
      {showInviteForm && (
        <div className="card bg-base-200 shadow-md">
          <div className="card-body">
            <h3 className="card-title text-base">Invite New User</h3>
            <form onSubmit={handleInvite} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Email</span>
                </label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input input-bordered input-sm w-full"
                  placeholder="user@example.com"
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name</span>
                </label>
                <input
                  type="text"
                  required
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="input input-bordered input-sm w-full"
                  placeholder="Full Name"
                />
              </div>
              <div className="md:col-span-2 flex gap-2 justify-end">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowInviteForm(false)}
                >
                  Cancel
                </button>
                <button type="submit" disabled={inviting} className="btn btn-primary btn-sm gap-2">
                  {inviting ? (
                    <span className="loading loading-spinner loading-xs" />
                  ) : (
                    <PlusIcon className="h-4 w-4" />
                  )}
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card bg-base-200 shadow-md">
        <div className="card-body">
          <h3 className="card-title text-base">Users ({users.length})</h3>
          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Orgs</th>
                  <th>MFA</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="font-medium">{u.name}</td>
                    <td className="text-base-content/70">{u.email}</td>
                    <td>
                      <span className={`badge badge-sm ${roleBadgeClass(u.role)}`}>
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td>
                      {u.orgAccess.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {u.orgAccess.map((o) => (
                            <span key={o.orgId} className="badge badge-sm badge-ghost">
                              {getOrgName(o.orgId)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-base-content/40">-</span>
                      )}
                    </td>
                    <td>
                      {u.mfaEnabled ? (
                        <span className="badge badge-sm badge-success">Enabled</span>
                      ) : (
                        <span className="badge badge-sm badge-warning">Disabled</span>
                      )}
                    </td>
                    <td>
                      {u.isActive ? (
                        <span className="badge badge-sm badge-success">Active</span>
                      ) : (
                        <span className="badge badge-sm badge-error">Inactive</span>
                      )}
                    </td>
                    <td className="text-base-content/60 text-xs">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      {u.isSystemAdmin ? (
                        <span className="text-base-content/40 text-xs">Protected</span>
                      ) : (
                        <div className="flex gap-1">
                          <button
                            className="btn btn-ghost btn-xs"
                            title="Edit"
                            onClick={() => openEditModal(u)}
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="btn btn-ghost btn-xs"
                            title={u.isActive ? 'Deactivate' : 'Reactivate'}
                            onClick={() => handleToggleActive(u)}
                            disabled={currentUser?.id === u.id}
                          >
                            {u.isActive ? (
                              <XMarkIcon className="h-3.5 w-3.5 text-warning" />
                            ) : (
                              <ArrowPathIcon className="h-3.5 w-3.5 text-success" />
                            )}
                          </button>
                          <button
                            className="btn btn-ghost btn-xs"
                            title="Delete"
                            onClick={() => handleDelete(u.id)}
                            disabled={currentUser?.id === u.id}
                          >
                            <TrashIcon className="h-3.5 w-3.5 text-error" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="card bg-base-200 shadow-md">
          <div className="card-body">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowInvitations(!showInvitations)}
            >
              <h3 className="card-title text-base">
                Pending Invitations ({pendingInvitations.length})
              </h3>
              <span className="text-base-content/40 text-sm">
                {showInvitations ? 'Collapse' : 'Expand'}
              </span>
            </div>
            {showInvitations && (
              <div className="overflow-x-auto mt-2">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvitations.map((inv) => (
                      <tr key={inv.id}>
                        <td>{inv.email}</td>
                        <td>{inv.name}</td>
                        <td>
                          <span className={`badge badge-sm ${roleBadgeClass(inv.role)}`}>
                            {roleLabel(inv.role)}
                          </span>
                        </td>
                        <td className="text-base-content/60 text-xs">
                          {new Date(inv.expiresAt).toLocaleString()}
                        </td>
                        <td>
                          <div className="flex gap-1">
                            <button
                              className="btn btn-ghost btn-xs gap-1"
                              onClick={() => handleResendInvitation(inv.id)}
                            >
                              <ArrowPathIcon className="h-3.5 w-3.5" />
                              Resend
                            </button>
                            <button
                              className="btn btn-ghost btn-xs gap-1 text-error"
                              onClick={() => handleCancelInvitation(inv.id)}
                            >
                              <XMarkIcon className="h-3.5 w-3.5" />
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Edit User: {editUser.email}</h3>
            <div className="space-y-4 mt-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name</span>
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="input input-bordered input-sm w-full"
                />
              </div>
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="toggle toggle-sm"
                    checked={editIsActive}
                    onChange={(e) => setEditIsActive(e.target.checked)}
                    disabled={currentUser?.id === editUser.id}
                  />
                  <span className="label-text">Active</span>
                </label>
              </div>
              {editUser.role !== 'site_admin' && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Organisations</span>
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-base-300 rounded-lg p-2 space-y-1">
                    {organisations.map((org) => (
                      <label key={org.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs"
                          checked={editOrgIds.includes(org.id)}
                          onChange={(e) =>
                            setEditOrgIds(
                              e.target.checked
                                ? [...editOrgIds, org.id]
                                : editOrgIds.filter((id) => id !== org.id)
                            )
                          }
                        />
                        <span className="text-sm">{org.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-action">
              <button className="btn btn-ghost btn-sm" onClick={() => setEditUser(null)}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleEditSave}>
                {saving ? <span className="loading loading-spinner loading-xs" /> : 'Save'}
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setEditUser(null)} />
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
